import { Box,Container, Card, Input, InputAdornment, Menu, CircularProgress, Link, Button, Select, Typography, Stack, MenuItem, IconButton, Tooltip, FormLabel, FormControl, Divider, RadioGroup, FormControlLabel, Radio, Checkbox, Popover } from "@mui/material";
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { MapTypeSelector, useMapTypeSelector } from "src/pages/app/eld/eld-landmarks/eld-landmark-map-modal";
import L from "leaflet";
import 'leaflet-providers';
import 'leaflet/dist/leaflet.css';
import Logo from 'src/components/logo';
import NoneFound from "src/components/none-found";
import { useParams } from "react-router";
import { Device, DeviceEldDriver, DeviceWithMapInfo } from "src/models";
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import useModelData from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";
import { useSettingsContext } from 'src/components/settings';
import { usePrevious } from "src/hooks/use-previous";
import Iconify from "src/components/iconify";
import { useLocalStorage } from "src/hooks/use-local-storage";
import { AdvancedMarker, APIProvider, InfoWindow, useMap } from "@vis.gl/react-google-maps";
import { GOOGLE_MAPS_API_KEY } from "src/config-global";
import { Marker, MarkerClusterer, SuperClusterOptions } from "@googlemaps/markerclusterer";
import { ClusterRenderer, GoogleMapWrapper, MAP_TYPES, MarkerData, TrafficLayer, TreeMarkerProps, WeighStationsLayer } from "../app/devices/map-google";

export const MAP_OVERLAYS = [
  { key: 'traffic', label: 'Traffic' },
  { key: 'weighStation', label: 'Weigh Stations' },
] as const;

type PageModel = {
  deviceDataResponse: {device?: Device, devices?: Device[], organizationName?: string} | {message: string},
}

export default function Page() {
  const settings = useSettingsContext();
  const { shareLinkUuid } = useParams();

  const dataLoader = useCallback(() => ({
    deviceDataResponse: axios.get(formatEndpoint(endpoints.devices.findByShareLinkUuid, {shareLinkUuid})).then(response => response.data),
  }), [shareLinkUuid]);

  const {
    data,
    updateData,
    setData,
    loaded,
    error,
    loading,
    setShouldRefresh,
  } = useModelData<PageModel>(dataLoader);


  return <Container maxWidth={settings.themeStretch ? false : 'lg'} sx={{p: 0}}>
    {data && !('message' in data.deviceDataResponse) && <Box height="55px" sx={{display: "flex", gap: 2, justifyContent: "space-between", alignItems: "center"}}>
      <Logo dark sx={{alignItems: "center"}} />
      <Box sx={{fontWeight: 800}}>{data.deviceDataResponse.organizationName ? data.deviceDataResponse.organizationName : ""}</Box>
    </Box>}
    <DisplayPageData loading={loading} error={error} data={data}>
      {data && <>
        {'message' in data.deviceDataResponse
          ? <Box p={4}>
            <NoneFound title={data.deviceDataResponse.message} message="Please contact the owner of the Account">
              <Logo dark />
            </NoneFound>
          </Box>
          :
            <Box position="absolute" left="0px" top="55px" right="0px" bottom="-15px" display="flex" flexDirection="row">
              <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                <DeviceMapGoogle devices={data.deviceDataResponse.device ? [data.deviceDataResponse.device] : data.deviceDataResponse.devices!}/>
              </APIProvider>
            </Box>
           }
      </>}
    </DisplayPageData>
  </Container>;
}



function DeviceMapGoogle({ devices }: { devices: Device[]}) {
  const [openInfoWindowUuid, setOpenInfoWindowUuid] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  const mapRef = useRef<google.maps.Map | null>(null);
  const [selectedUuid, setSelectedUuid] = useState("");
  const [mapOptionsOpen, setMapOptionsOpen] = useState<HTMLElement | null>(null);
    const { state: mapSettings, update: updateMapSettings } = useLocalStorage('trux-map-settings', {
      mapType: 'default',
      selectedOptions: []
    });
  const {selectedOptions} = mapSettings;
  const setSelectedOptions = (options: string[]) => updateMapSettings('selectedOptions', options);
  const {mapType} = mapSettings;
  const setMapType = (type: string) => updateMapSettings('mapType', type);

  const selectedUuidPrev = usePrevious(selectedUuid);
  // Prepare marker data
  const markers: MarkerData[] = useMemo(() => devices
      .filter(device => device.lastPosLat && device.lastPosLong && device.lastPing)
      .map(device => ({
        device: {...device, location: ""},
        position: {
          lat: Number(device.lastPosLat),
          lng: Number(device.lastPosLong)
        }
      })), [devices]);

  // Open info window for selected device
  useEffect(() => {
    if (selectedUuid && selectedUuid !== selectedUuidPrev) {
      setOpenInfoWindowUuid(selectedUuid);
      const marker = markers.find(m => m.device.deviceUuid === selectedUuid);
      if (marker && mapRef.current) {
        mapRef.current.panTo(marker.position);
        if ((mapRef.current.getZoom() || 100) < 12) {
          mapRef.current.setZoom(12);
        }
      }
    }
  }, [selectedUuid, markers, mapRef, selectedUuidPrev]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  const onToggleFullScreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
    }
  }, []);


  const handleMarkerClick = useCallback((device: Device) => {
    setSelectedUuid(device.deviceUuid);
  }, [setSelectedUuid]);

  return (
    <>
      <Box sx={{ flexGrow: 1, position: "relative" }}>
        <Box sx={{
          position: "absolute",
          top: "20px",
          right: "20px",
          zIndex: "1000",
          borderRadius: "4px",
          display: "flex",
          flexDirection: "column",
          alignItems: "end",
          gap: "10px"
        }}>
          <Box>
             <Tooltip title="Map Options" arrow>
               <Button
                 className="map-overlay-element"
                 variant="contained"
                 sx={{minWidth: "initial", background: "#FEFEFE", color: "black", "&:hover": {background: "#DEDEDE"}, boxShadow: "0 0 10px rgba(0,0,0,0.2)" }}
                 onClick={(e) => setMapOptionsOpen(e.currentTarget)}
               >
                 <Iconify icon="material-symbols-light:layers-outline" />
               </Button>
             </Tooltip>
           </Box>

          <Box>
            <Tooltip title="Toggle fullscreen" arrow>
              <Button
                className="map-overlay-element"
                variant="contained"
                onClick={onToggleFullScreen}
                sx={{minWidth: "initial", background: "#FEFEFE", color: "black", "&:hover": {background: "#DEDEDE"}, boxShadow: "0 0 10px rgba(0,0,0,0.2)" }}
              >
                <Iconify icon={isFullscreen ? "material-symbols-light:fullscreen-exit" : "material-symbols-light:fullscreen"} />
              </Button>
            </Tooltip>
          </Box>
        </Box>


        {/* Zoom controls */}
        <Box sx={{
          position: "absolute",
          top: "20px",
          left: "20px",
          zIndex: "1000",
          background: "white",
          borderRadius: "3px",
          boxShadow: "0 0 10px rgba(0,0,0,0.2)",
          border: "1px solid rgba(145, 158, 171, 0.32)",
          display: "flex",
          flexDirection: "column",
        }}>
          <IconButton size="small" onClick={() => {
            if (mapRef.current) {
              const currentZoom = mapRef.current.getZoom();
              if (currentZoom !== undefined) {
                mapRef.current.setZoom(currentZoom + 1);
              }
            }
          }}>
            <Iconify icon="mdi:plus" />
          </IconButton>
          <Box sx={{ borderTop: "1px solid #e0e0e0" }} />
          <IconButton size="small" onClick={() => {
            if (mapRef.current) {
              const currentZoom = mapRef.current.getZoom();
              if (currentZoom !== undefined) {
                mapRef.current.setZoom(currentZoom - 1);
              }
            }
          }}>
            <Iconify icon="mdi:minus" />
          </IconButton>
        </Box>

        {/* Map */}
        <GoogleMapWrapper
          mapType={mapType}
          mapRef={mapRef}
        >

          {/* Weigh stations layer */}
          {selectedOptions.includes("weighStation") && (
            <WeighStationsLayer />
          )}

          {selectedOptions.includes("traffic") && (
            <TrafficLayer />
          )}

          {/* Device markers with clustering */}
          <MarkerClustererComponentShare
            markersData={markers}
            openInfoWindowUuid={openInfoWindowUuid}
            setOpenInfoWindowUuid={setOpenInfoWindowUuid}
            onTruckMarkerClick={handleMarkerClick}
          />
        </GoogleMapWrapper>
      </Box>

      {/* Map Options Menu */}
      <Popover
        open={Boolean(mapOptionsOpen)}
        anchorEl={mapOptionsOpen}
        onClose={() => setMapOptionsOpen(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            minWidth: 200,
            maxWidth: 320,
            p: 2,
            borderRadius: 0.8,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            marginTop: "3px"
          }
        }}
      >
        <Stack spacing={2}>
          {/* Map Type Selection */}
          <FormControl component="fieldset">
            <FormLabel sx={{
              fontWeight: 600,
              color: 'text.secondary',
              mb: 1,
              '&.Mui-focused': {
                color: 'text.secondary'
              },
              '&.MuiFormLabel-filled': {
                color: 'text.secondary'
              }
            }}>
              MAP BASE
            </FormLabel>
              <RadioGroup
                value={mapType}
                onChange={(e) => setMapType(e.target.value as 'default' | 'satellite' | 'terrain')}
                sx={{ paddingLeft: "2px" }}
              >
                {MAP_TYPES.map((type) => (
                  <FormControlLabel
                    key={type.key}
                    value={type.key}
                    control={<Radio size="small" />}
                    label={type.label}
                  />
                ))}
              </RadioGroup>
          </FormControl>

          <Divider />

          {/* Overlay Options */}
          <FormControl component="fieldset">
            <FormLabel sx={{
              fontWeight: 600,
              color: 'text.secondary',
              mb: 2,
              '&.Mui-focused': {
                color: 'text.secondary'
              },
              '&.MuiFormLabel-filled': {
                color: 'text.secondary'
              }
            }}>
              OVERLAYS
            </FormLabel>
            <Stack spacing={2} paddingLeft="8px">
              {MAP_OVERLAYS.map((overlay) => (
                <FormControlLabel
                  key={overlay.key}
                  control={
                    <Checkbox
                      size="small"
                      sx={{ paddingX: "6px" }}
                      checked={selectedOptions.includes(overlay.key)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedOptions([...selectedOptions, overlay.key]);
                        } else {
                          setSelectedOptions(selectedOptions.filter((option: string) => option !== overlay.key));
                        }
                      }}
                    />
                  }
                  label={overlay.label}
                />
              ))}
            </Stack>
          </FormControl>
        </Stack>
      </Popover>
    </>
  );
}


export function MarkerClustererComponentShare({
  markersData,
  openInfoWindowUuid,
  setOpenInfoWindowUuid,
  onTruckMarkerClick
}: {
  markersData: MarkerData[],
  openInfoWindowUuid: string | null,
  setOpenInfoWindowUuid: (uuid: string | null) => void,
  onTruckMarkerClick: (device: DeviceWithMapInfo) => void,
}) {
  const map = useMap();

  const clusterer = useMemo(() => {
    if (!map) return null;

    return new MarkerClusterer({
          map,
          markers: [],
          algorithmOptions: {
            maxZoom: 10, // Disable clustering at zoom 10+
            radius: 120,
          } as SuperClusterOptions,
          onClusterClick: (e, c) => {
            map.setCenter(c.position)
            if (c.bounds) {
              map.fitBounds(c.bounds, 40)
            }
          },
          renderer: new ClusterRenderer()
        })
  }, [map]);

  const [markers, setMarkers] = useState<{[key: string]: Marker}>({});

  const selectedMarker = useMemo(
    () =>
      markersData && openInfoWindowUuid
        ? markersData.find(t => t.device.deviceUuid === openInfoWindowUuid)!
        : null,
    [markersData, openInfoWindowUuid]
  );

  useEffect(() => {
    if (!clusterer) return;

    clusterer.clearMarkers();
    clusterer.addMarkers(Object.values(markers));
  }, [clusterer, markers]);

  // this callback will effectively get passsed as ref to the markers to keep
  // tracks of markers currently on the map
  const setMarkerRef = useCallback((marker: Marker | null, key: string) => {
    setMarkers(markersList => {
      if ((marker && markersList[key]) || (!marker && !markersList[key]))
        return markersList;

      if (marker) {
        return {...markersList, [key]: marker};
      }
        const {[key]: _, ...newMarkers} = markersList;

        return newMarkers;

    });
  }, []);

  const handleInfoWindowClose = useCallback(() => {
    setOpenInfoWindowUuid(null);
  }, [setOpenInfoWindowUuid]);

  const handleMarkerClick = useCallback((markerData: MarkerData) => {
    onTruckMarkerClick(markerData.device);
    setOpenInfoWindowUuid(markerData.device.deviceUuid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onTruckMarkerClick]);

  return (
    <>
      {markersData.map((markerData) => (
        <CustomMapMarkerShare
          key={markerData.device.deviceUuid}
          markerData={markerData}
          setMarkerRef={setMarkerRef}
          onClick={handleMarkerClick}
        />
      ))}

      {openInfoWindowUuid && selectedMarker?.device && (
        <InfoWindow
          disableAutoPan
          headerContent={<DeviceInfoCardHeader device={selectedMarker.device} />}
          anchor={markers[openInfoWindowUuid]}
          pixelOffset={[0, -18]}
          shouldFocus={false}
          onCloseClick={handleInfoWindowClose}>
          <DeviceInfoCard device={selectedMarker.device} />
        </InfoWindow>
      )}
    </>
  );
}

function DeviceInfoCardHeader({ device }: {device: DeviceWithMapInfo }) {
  const isTruck = device.deviceType.startsWith("ELD Truck #");
  return <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0px' }}>
    <div style={{ display: 'flex', justifyContent: 'center', marginRight: '10px', width: '30px', textAlign: 'center' }}>
      {isTruck ? (
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="24" viewBox="0 0 36 36">
          <path fill="currentColor" d="M30 12h-4V7a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v18a1 1 0 0 0 1 1h1V8h20v11.7a6.5 6.5 0 0 1 1.56-.2H26V14h4a2 2 0 0 1 2 2v1h-4v2h4v5h-2.4a4.54 4.54 0 0 0-8.34 0h-6.83a4.5 4.5 0 0 0-4.17-2.76A4.38 4.38 0 1 0 14.72 26H21a4.49 4.49 0 0 0 8.92 0H33a1 1 0 0 0 1-1v-9a4 4 0 0 0-4-4M10.26 28a2.38 2.38 0 1 1 0-4.75a2.38 2.38 0 1 1 0 4.75m15.17 0a2.38 2.38 0 1 1 2.5-2.37a2.44 2.44 0 0 1-2.5 2.37" />
          <path fill="none" d="M0 0h36v36H0z"/>
        </svg>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" width="28" style={{ marginLeft: '2px' }} height="24" viewBox="0 0 36 36">
          <path fill="currentColor" d="M15 19.2c-3.2 0-5.8 2.6-5.8 5.8s2.6 5.8 5.8 5.8s5.8-2.6 5.8-5.8s-2.6-5.8-5.8-5.8m0 10c-2.3 0-4.2-1.9-4.2-4.2s1.9-4.2 4.2-4.2s4.2 1.9 4.2 4.2s-1.9 4.2-4.2 4.2" />
          <path fill="currentColor" d="M14 24h2v2h-2z" />
          <path fill="currentColor" d="M33 9H2v13.1C2 24.3 3.7 26 5.9 26H7v-2H5.9c-1 0-1.8-.8-1.9-1.9V15h22v7.1c0 1-.8 1.8-1.9 1.9H23v2h1.1c2.1 0 3.8-1.7 3.8-3.9V11h5c.6 0 1-.4 1-1s-.3-1-.9-1m-7 4H4v-2h22z" />
          <path fill="none" d="M0 0h36v36H0z"/>
        </svg>
      )}
    </div>
    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{device.deviceType}</div>
  </div>;
}

function DeviceInfoCard({ device }: {device: DeviceWithMapInfo }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '7px' }} />

      <hr style={{ border: 'none', height: '1px', backgroundColor: '#e0e0e0', margin: '7px 0' }}/>

      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '7px' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginRight: '10px', width: '30px', textAlign: 'center' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <g fill="none" stroke="#777" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5">
              <path d="M12.56 20.82a.96.96 0 0 1-1.12 0C6.611 17.378 1.486 10.298 6.667 5.182A7.6 7.6 0 0 1 12 3c2 0 3.919.785 5.333 2.181c5.181 5.116.056 12.196-4.773 15.64"/>
              <path d="M12 12a2 2 0 1 0 0-4a2 2 0 0 0 0 4"/>
            </g>
          </svg>
        </div>
        <div style={{ fontSize: '14px', color: '#777' }}>
          {device.location && (
            <>
              {device.location}
              <br />
            </>
          )}
          <a href={`https://maps.google.com/?q=${device.lastPosLat},${device.lastPosLong}`}>
            {device.lastPosLat}, {device.lastPosLong}
          </a>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '7px' }} />
    </div>
  );
};

export const CustomMapMarkerShare = (props: TreeMarkerProps) => {
  const {markerData, onClick, setMarkerRef} = props;

  const handleClick = useCallback(() => onClick(markerData), [onClick, markerData]);
  const ref = useCallback(
    (marker: google.maps.marker.AdvancedMarkerElement) =>
      setMarkerRef(marker, markerData.device.deviceUuid),
    [setMarkerRef, markerData.device.deviceUuid]
  );

  const isArrowMarker = markerData.device.direction !== null && markerData.device.direction !== undefined && markerData.device.direction > -1 && markerData.device.direction !== 0;

  return (
    <AdvancedMarker position={markerData.position} anchorPoint={isArrowMarker ? ["14px", "14px"] : ["7px", "7px"]} ref={ref} onClick={handleClick}>
      <div style={{ position: 'relative' }}>
        <div style={{
          backgroundColor: '#1D1D1D',
          color: 'white',
          fontSize: '10px',
          fontWeight: '500',
          padding: '2px 6px',
          borderRadius: '4px',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
          position: 'absolute',
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginBottom: '2px'
        }}>
          {markerData.device.deviceType.startsWith("ELD Truck #") ? `${markerData.device.deviceType.replace("ELD Truck #", "")}` : `${markerData.device.deviceType}`}
        </div>
        <RenderElementShare device={markerData.device} />
      </div>
    </AdvancedMarker>
  );
};
function RenderElementShare({device}: {device: DeviceWithMapInfo}) {
  const squareConnected = <svg xmlns="http://www.w3.org/2000/svg" width="14px" height="14px" viewBox="0 0 56 56">
    <rect x="3" y="3" width="50" height="50" rx="6" fill="#36b746" stroke="white" strokeWidth="3"/>
  </svg>;

  const getArrowConnected = (direction: number) => (
    <svg viewBox="0 0 100 100" width="28px" height="28px" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="1" stdDeviation="2" floodOpacity="0.2"/>
        </filter>
      </defs>
      <path
        d="M50,10 L85,75 L50,55 L15,75 Z"
        fill="#4CAF50"
        stroke="#FFFFFF"
        strokeWidth="4"
        strokeLinejoin="round"
        filter="url(#shadow)"
        transform={`rotate(${direction || 0} 50 50)`}
      />
    </svg>
  );

  // eslint-disable-next-line no-nested-ternary
  return device.direction !== null && device.direction !== undefined && device.direction > -1 && device.direction !== 0
                ? getArrowConnected(device.direction)
                : squareConnected;
}